import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, MapPin, Clock, Users, DollarSign, Star, Info, CheckCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import CityTourBookingForm from '../components/CityTourBookingForm';

interface TourDetails {
  title: string;
  price: number;
  duration: string;
}

const UgandaCityTours = () => {
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [selectedTour, setSelectedTour] = useState<TourDetails | null>(null);
  const { t } = useTranslation();

  const handleBookNow = (tour: TourDetails) => {
    setSelectedTour(tour);
    setIsBookingModalOpen(true);
  };

  return (
    <>
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative bg-gray-900 text-white">
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1589579234047-006271d2688d?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2574&q=80" 
            alt="Uganda cityscape" 
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-black/60"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 py-12 sm:px-6 lg:px-8">
          <div className="mb-4">
            <Link to="/" className="inline-flex items-center text-white/90 hover:text-white">
              <ArrowLeft className="h-4 w-4 mr-1" />
              {t('common.back')}
            </Link>
          </div>
          
          <div className="max-w-3xl">
            <h1 className="text-3xl md:text-4xl font-bold mb-4">{t('pages.cityTours.title')}</h1>
            <p className="text-lg text-white/90 mb-6">
              {t('pages.cityTours.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Kampala Tours */}
      <section className="py-8 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-xl font-bold text-gray-900 mb-6">{t('pages.cityTours.kampala')}</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {/* Walking Tour */}
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-md transition-all duration-300">
              <div className="relative h-32 sm:h-36">
                <img 
                  src="https://imgur.com/LTxFu0W.jpg" 
                  alt="Gaddafi Mosque Tour" 
                  className="w-full h-full object-cover"
                />
                <div className="absolute bottom-2 left-2 bg-ta-green text-white text-[10px] px-1.5 py-0.5 rounded-full">
                  Most Popular
                </div>
              </div>
              <div className="p-2 sm:p-3">
                <h3 className="text-sm font-medium text-gray-900 mb-1">Walking Tour with Gaddafi Mosque</h3>
                <div className="flex items-center mb-1">
                  <Star className="h-3 w-3 text-yellow-400 fill-current" />
                  <span className="ml-1 text-xs font-medium">4.8</span>
                  <span className="mx-1 text-gray-400 text-xs">•</span>
                  <span className="text-xs text-gray-600">124 {t('common.reviews')}</span>
                </div>
                <div className="space-y-1 mb-2">
                  <div className="flex items-center text-xs text-gray-600">
                    <Clock className="h-3 w-3 text-ta-green mr-1" />
                    <span>3 hours</span>
                  </div>
                  <div className="flex items-center text-xs text-gray-600">
                    <DollarSign className="h-3 w-3 text-ta-green mr-1" />
                    <span>{t('common.from')} $32</span>
                  </div>
                </div>
                <div className="flex space-x-2">
                  <Link 
                    to="/tours/kampala-walking"
                    className="flex-1 py-1.5 text-center bg-gray-100 text-gray-700 text-xs rounded-md hover:bg-gray-200 transition-colors"
                  >
                    {t('experiences.viewDetails')}
                  </Link>
                  <button 
                    onClick={() => handleBookNow({
                      title: "Walking Tour with Gaddafi Mosque",
                      price: 32,
                      duration: "3 hours"
                    })}
                    className="flex-1 py-1.5 bg-ta-green text-white text-xs rounded-md hover:bg-ta-green-dark transition-colors"
                  >
                    {t('pages.cityTours.bookNow')}
                  </button>
                </div>
              </div>
            </div>

            {/* Complete Kampala Tour */}
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-md transition-all duration-300">
              <div className="relative h-32 sm:h-36">
                <img 
                  src="https://imgur.com/EzHnSsj.jpg" 
                  alt="Complete Kampala Tour" 
                  className="w-full h-full object-cover"
                />
                <div className="absolute bottom-2 left-2 bg-ta-green text-white text-[10px] px-1.5 py-0.5 rounded-full">
                  Best Value
                </div>
              </div>
              <div className="p-2 sm:p-3">
                <h3 className="text-sm font-medium text-gray-900 mb-1">Complete Kampala Experience</h3>
                <div className="flex items-center mb-1">
                  <Star className="h-3 w-3 text-yellow-400 fill-current" />
                  <span className="ml-1 text-xs font-medium">4.9</span>
                  <span className="mx-1 text-gray-400 text-xs">•</span>
                  <span className="text-xs text-gray-600">98 {t('common.reviews')}</span>
                </div>
                <div className="space-y-1 mb-2">
                  <div className="flex items-center text-xs text-gray-600">
                    <Clock className="h-3 w-3 text-ta-green mr-1" />
                    <span>Full Day</span>
                  </div>
                  <div className="flex items-center text-xs text-gray-600">
                    <DollarSign className="h-3 w-3 text-ta-green mr-1" />
                    <span>{t('common.from')} $25</span>
                  </div>
                </div>
                <div className="flex space-x-2">
                  <Link 
                    to="/tours/kampala-complete"
                    className="flex-1 py-1.5 text-center bg-gray-100 text-gray-700 text-xs rounded-md hover:bg-gray-200 transition-colors"
                  >
                    {t('experiences.viewDetails')}
                  </Link>
                  <button 
                    onClick={() => handleBookNow({
                      title: "Complete Kampala Experience",
                      price: 25,
                      duration: "Full Day"
                    })}
                    className="flex-1 py-1.5 bg-ta-green text-white text-xs rounded-md hover:bg-ta-green-dark transition-colors"
                  >
                    {t('pages.cityTours.bookNow')}
                  </button>
                </div>
              </div>
            </div>

            {/* Food Tour */}
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-md transition-all duration-300">
              <div className="relative h-32 sm:h-36">
                <img 
                  src="https://imgur.com/4kwMnNu.jpg" 
                  alt="Kampala Food Tour" 
                  className="w-full h-full object-cover"
                />
                <div className="absolute bottom-2 left-2 bg-orange-500 text-white text-[10px] px-1.5 py-0.5 rounded-full">
                  Food & Culture
                </div>
              </div>
              <div className="p-2 sm:p-3">
                <h3 className="text-sm font-medium text-gray-900 mb-1">City Walking Food Tour</h3>
                <div className="flex items-center mb-1">
                  <Star className="h-3 w-3 text-yellow-400 fill-current" />
                  <span className="ml-1 text-xs font-medium">4.7</span>
                  <span className="mx-1 text-gray-400 text-xs">•</span>
                  <span className="text-xs text-gray-600">76 {t('common.reviews')}</span>
                </div>
                <div className="space-y-1 mb-2">
                  <div className="flex items-center text-xs text-gray-600">
                    <Clock className="h-3 w-3 text-ta-green mr-1" />
                    <span>4 hours</span>
                  </div>
                  <div className="flex items-center text-xs text-gray-600">
                    <DollarSign className="h-3 w-3 text-ta-green mr-1" />
                    <span>{t('common.from')} $77</span>
                  </div>
                </div>
                <div className="flex space-x-2">
                  <Link 
                    to="/tours/kampala-food"
                    className="flex-1 py-1.5 text-center bg-gray-100 text-gray-700 text-xs rounded-md hover:bg-gray-200 transition-colors"
                  >
                    {t('experiences.viewDetails')}
                  </Link>
                  <button 
                    onClick={() => handleBookNow({
                      title: "City Walking Food Tour",
                      price: 77,
                      duration: "4 hours"
                    })}
                    className="flex-1 py-1.5 bg-ta-green text-white text-xs rounded-md hover:bg-ta-green-dark transition-colors"
                  >
                    {t('pages.cityTours.bookNow')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Entebbe Tours */}
      <section className="py-8 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-xl font-bold text-gray-900 mb-6">{t('pages.cityTours.entebbe')}</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-md transition-all duration-300">
              <div className="relative h-32 sm:h-36">
                <img 
                  src="https://imgur.com/ELruAkr.jpg" 
                  alt="Entebbe City Tour" 
                  className="w-full h-full object-cover"
                />
                <div className="absolute bottom-2 left-2 bg-ta-green text-white text-[10px] px-1.5 py-0.5 rounded-full">
                  Recommended
                </div>
              </div>
              <div className="p-2 sm:p-3">
                <h3 className="text-sm font-medium text-gray-900 mb-1">1-Day Entebbe Experience</h3>
                <div className="flex items-center mb-1">
                  <Star className="h-3 w-3 text-yellow-400 fill-current" />
                  <span className="ml-1 text-xs font-medium">4.8</span>
                  <span className="mx-1 text-gray-400 text-xs">•</span>
                  <span className="text-xs text-gray-600">89 {t('common.reviews')}</span>
                </div>
                <div className="space-y-1 mb-2">
                  <div className="flex items-center text-xs text-gray-600">
                    <Clock className="h-3 w-3 text-ta-green mr-1" />
                    <span>Full Day</span>
                  </div>
                  <div className="flex items-center text-xs text-gray-600">
                    <DollarSign className="h-3 w-3 text-ta-green mr-1" />
                    <span>Solo: $196 | Group: $139</span>
                  </div>
                </div>
                <div className="flex space-x-2">
                  <Link 
                    to="/tours/entebbe-day"
                    className="flex-1 py-1.5 text-center bg-gray-100 text-gray-700 text-xs rounded-md hover:bg-gray-200 transition-colors"
                  >
                    {t('experiences.viewDetails')}
                  </Link>
                  <button 
                    onClick={() => handleBookNow({
                      title: "1-Day Entebbe Experience",
                      price: 196,
                      duration: "Full Day"
                    })}
                    className="flex-1 py-1.5 bg-ta-green text-white text-xs rounded-md hover:bg-ta-green-dark transition-colors"
                  >
                    {t('pages.cityTours.bookNow')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Jinja Tours */}
      <section className="py-8 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-xl font-bold text-gray-900 mb-6">{t('pages.cityTours.jinja')}</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {/* White Water Rafting */}
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-md transition-all duration-300">
              <div className="relative h-32 sm:h-36">
                <img 
                  src="https://imgur.com/tzBxU2P.jpg" 
                  alt="White Water Rafting" 
                  className="w-full h-full object-cover"
                />
                <div className="absolute bottom-2 left-2 bg-blue-500 text-white text-[10px] px-1.5 py-0.5 rounded-full">
                  Water Adventure
                </div>
              </div>
              <div className="p-2 sm:p-3">
                <h3 className="text-sm font-medium text-gray-900 mb-1">White Water Rafting</h3>
                <p className="text-xs text-gray-600 mb-1">Experience thrilling Grade 5 rapids on the Nile!</p>
                <div className="flex items-center mb-1">
                  <Star className="h-3 w-3 text-yellow-400 fill-current" />
                  <span className="ml-1 text-xs font-medium">4.9</span>
                  <span className="mx-1 text-gray-400 text-xs">•</span>
                  <span className="text-xs text-gray-600">156 {t('common.reviews')}</span>
                </div>
                <div className="space-y-1 mb-2">
                  <div className="flex items-center text-xs text-gray-600">
                    <Clock className="h-3 w-3 text-ta-green mr-1" />
                    <span>Full Day</span>
                  </div>
                  <div className="flex items-center text-xs text-gray-600">
                    <DollarSign className="h-3 w-3 text-ta-green mr-1" />
                    <span>{t('common.from')} $160</span>
                  </div>
                </div>
                <div className="flex space-x-2">
                  <Link 
                    to="/tours/jinja-rafting"
                    className="flex-1 py-1.5 text-center bg-gray-100 text-gray-700 text-xs rounded-md hover:bg-gray-200 transition-colors"
                  >
                    {t('experiences.viewDetails')}
                  </Link>
                  <button 
                    onClick={() => handleBookNow({
                      title: "White Water Rafting",
                      price: 160,
                      duration: "Full Day"
                    })}
                    className="flex-1 py-1.5 bg-ta-green text-white text-xs rounded-md hover:bg-ta-green-dark transition-colors"
                  >
                    {t('pages.cityTours.bookNow')}
                  </button>
                </div>
              </div>
            </div>

            {/* Bungee Jumping */}
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-md transition-all duration-300">
              <div className="relative h-32 sm:h-36">
                <img 
                  src="https://imgur.com/Z97JH4P.jpg" 
                  alt="Bungee Jumping" 
                  className="w-full h-full object-cover"
                />
                <div className="absolute bottom-2 left-2 bg-red-500 text-white text-[10px] px-1.5 py-0.5 rounded-full">
                  Extreme Adventure
                </div>
              </div>
              <div className="p-2 sm:p-3">
                <h3 className="text-sm font-medium text-gray-900 mb-1">Bungee Jumping</h3>
                <p className="text-xs text-gray-600 mb-1">Jump from a 44-meter platform over the Nile!</p>
                <div className="flex items-center mb-1">
                  <Star className="h-3 w-3 text-yellow-400 fill-current" />
                  <span className="ml-1 text-xs font-medium">4.8</span>
                  <span className="mx-1 text-gray-400 text-xs">•</span>
                  <span className="text-xs text-gray-600">98 {t('common.reviews')}</span>
                </div>
                <div className="space-y-1 mb-2">
                  <div className="flex items-center text-xs text-gray-600">
                    <Clock className="h-3 w-3 text-ta-green mr-1" />
                    <span>2 hours</span>
                  </div>
                  <div className="flex items-center text-xs text-gray-600">
                    <DollarSign className="h-3 w-3 text-ta-green mr-1" />
                    <span>{t('common.from')} $120 (Foreign) | $100 (EA)</span>
                  </div>
                </div>
                <div className="flex space-x-2">
                  <Link 
                    to="/tours/jinja-bungee"
                    className="flex-1 py-1.5 text-center bg-gray-100 text-gray-700 text-xs rounded-md hover:bg-gray-200 transition-colors"
                  >
                    {t('experiences.viewDetails')}
                  </Link>
                  <button 
                    onClick={() => handleBookNow({
                      title: "Bungee Jumping",
                      price: 120,
                      duration: "2 hours"
                    })}
                    className="flex-1 py-1.5 bg-ta-green text-white text-xs rounded-md hover:bg-ta-green-dark transition-colors"
                  >
                    {t('pages.cityTours.bookNow')}
                  </button>
                </div>
              </div>
            </div>

            {/* Sunset Cruise */}
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-md transition-all duration-300">
              <div className="relative h-32 sm:h-36">
                <img 
                  src="https://imgur.com/ZhSOWHi.jpg" 
                  alt="Sunset Cruise" 
                  className="w-full h-full object-cover"
                />
                <div className="absolute bottom-2 left-2 bg-orange-500 text-white text-[10px] px-1.5 py-0.5 rounded-full">
                  Relaxation
                </div>
              </div>
              <div className="p-2 sm:p-3">
                <h3 className="text-sm font-medium text-gray-900 mb-1">Sunset Cruise</h3>
                <p className="text-xs text-gray-600 mb-1">Relax on the Nile with drinks and stunning views!</p>
                <div className="flex items-center mb-1">
                  <Star className="h-3 w-3 text-yellow-400 fill-current" />
                  <span className="ml-1 text-xs font-medium">4.7</span>
                  <span className="mx-1 text-gray-400 text-xs">•</span>
                  <span className="text-xs text-gray-600">124 {t('common.reviews')}</span>
                </div>
                <div className="space-y-1 mb-2">
                  <div className="flex items-center text-xs text-gray-600">
                    <Clock className="h-3 w-3 text-ta-green mr-1" />
                    <span>2 hours</span>
                  </div>
                  <div className="flex items-center text-xs text-gray-600">
                    <DollarSign className="h-3 w-3 text-ta-green mr-1" />
                    <span>{t('common.from')} $25</span>
                  </div>
                </div>
                <div className="flex space-x-2">
                  <Link 
                    to="/tours/jinja-sunset"
                    className="flex-1 py-1.5 text-center bg-gray-100 text-gray-700 text-xs rounded-md hover:bg-gray-200 transition-colors"
                  >
                    {t('experiences.viewDetails')}
                  </Link>
                  <button 
                    onClick={() => handleBookNow({
                      title: "Sunset Cruise",
                      price: 25,
                      duration: "2 hours"
                    })}
                    className="flex-1 py-1.5 bg-ta-green text-white text-xs rounded-md hover:bg-ta-green-dark transition-colors"
                  >
                    {t('pages.cityTours.bookNow')}
                  </button>
                </div>
              </div>
            </div>

            {/* Kayaking */}
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-md transition-all duration-300">
              <div className="relative h-32 sm:h-36">
                <img 
                  src="https://imgur.com/Wa3J7KL.jpg" 
                  alt="Kayaking" 
                  className="w-full h-full object-cover"
                />
                <div className="absolute bottom-2 left-2 bg-blue-500 text-white text-[10px] px-1.5 py-0.5 rounded-full">
                  Water Sport
                </div>
              </div>
              <div className="p-2 sm:p-3">
                <h3 className="text-sm font-medium text-gray-900 mb-1">Kayaking</h3>
                <p className="text-xs text-gray-600 mb-1">Paddle along the Nile with guided tours!</p>
                <div className="flex items-center mb-1">
                  <Star className="h-3 w-3 text-yellow-400 fill-current" />
                  <span className="ml-1 text-xs font-medium">4.8</span>
                  <span className="mx-1 text-gray-400 text-xs">•</span>
                  <span className="text-xs text-gray-600">86 {t('common.reviews')}</span>
                </div>
                <div className="space-y-1 mb-2">
                  <div className="flex items-center text-xs text-gray-600">
                    <Clock className="h-3 w-3 text-ta-green mr-1" />
                    <span>3 hours</span>
                  </div>
                  <div className="flex items-center text-xs text-gray-600">
                    <DollarSign className="h-3 w-3 text-ta-green mr-1" />
                    <span>{t('common.from')} $50</span>
                  </div>
                </div>
                <div className="flex space-x-2">
                  <Link 
                    to="/tours/jinja-kayaking"
                    className="flex-1 py-1.5 text-center bg-gray-100 text-gray-700 text-xs rounded-md hover:bg-gray-200 transition-colors"
                  >
                    {t('experiences.viewDetails')}
                  </Link>
                  <button 
                    onClick={() => handleBookNow({
                      title: "Kayaking",
                      price: 50,
                      duration: "3 hours"
                    })}
                    className="flex-1 py-1.5 bg-ta-green text-white text-xs rounded-md hover:bg-ta-green-dark transition-colors"
                  >
                    {t('pages.cityTours.bookNow')}
                  </button>
                </div>
              </div>
            </div>

            {/* Quad Biking */}
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-md transition-all duration-300">
              <div className="relative h-32 sm:h-36">
                <img 
                  src="https://imgur.com/IsRyKcu.jpg" 
                  alt="Quad Biking" 
                  className="w-full h-full object-cover"
                />
                <div className="absolute bottom-2 left-2 bg-yellow-500 text-white text-[10px] px-1.5 py-0.5 rounded-full">
                  Land Adventure
                </div>
              </div>
              <div className="p-2 sm:p-3">
                <h3 className="text-sm font-medium text-gray-900 mb-1">Quad Biking</h3>
                <p className="text-xs text-gray-600 mb-1">Explore Jinja's scenic routes on a thrilling adventure!</p>
                <div className="flex items-center mb-1">
                  <Star className="h-3 w-3 text-yellow-400 fill-current" />
                  <span className="ml-1 text-xs font-medium">4.9</span>
                  <span className="mx-1 text-gray-400 text-xs">•</span>
                  <span className="text-xs text-gray-600">92 {t('common.reviews')}</span>
                </div>
                <div className="space-y-1 mb-2">
                  <div className="flex items-center text-xs text-gray-600">
                    <Clock className="h-3 w-3 text-ta-green mr-1" />
                    <span>2 hours</span>
                  </div>
                  <div className="flex items-center text-xs text-gray-600">
                    <DollarSign className="h-3 w-3 text-ta-green mr-1" />
                    <span>{t('common.from')} $60</span>
                  </div>
                </div>
                <div className="flex space-x-2">
                  <Link 
                    to="/tours/jinja-quad-biking"
                    className="flex-1 py-1.5 text-center bg-gray-100 text-gray-700 text-xs rounded-md hover:bg-gray-200 transition-colors"
                  >
                    {t('experiences.viewDetails')}
                  </Link>
                  <button 
                    onClick={() => handleBookNow({
                      title: "Quad Biking",
                      price: 60,
                      duration: "2 hours"
                    })}
                    className="flex-1 py-1.5 bg-ta-green text-white text-xs rounded-md hover:bg-ta-green-dark transition-colors"
                  >
                    {t('pages.cityTours.bookNow')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Additional Information */}
      <section className="py-8 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-xl font-bold text-gray-900 mb-6">{t('common.additionalInfo')}</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('common.included')}</h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-ta-green mr-2 flex-shrink-0 mt-0.5" />
                  <span>{t('common.professionalGuide')}</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-ta-green mr-2 flex-shrink-0 mt-0.5" />
                  <span>{t('common.hotelPickup')}</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-ta-green mr-2 flex-shrink-0 mt-0.5" />
                  <span>{t('common.bottledWater')}</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-ta-green mr-2 flex-shrink-0 mt-0.5" />
                  <span>{t('common.entranceFees')}</span>
                </li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('common.importantInfo')}</h3>
              <div className="bg-white p-4 rounded-lg border border-gray-200">
                <div className="flex items-start mb-4">
                  <Info className="h-5 w-5 text-ta-green mr-2 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-gray-900">{t('common.bestTimeToVisit')}</h4>
                    <p className="text-gray-600 text-sm">{t('common.toursAvailableYearRound')}</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <Info className="h-5 w-5 text-ta-green mr-2 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-gray-900">{t('common.bookingInfo')}</h4>
                    <p className="text-gray-600 text-sm">{t('common.advanceBookingRecommended')}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Booking Form Modal */}
      {selectedTour && (
        <CityTourBookingForm
          isOpen={isBookingModalOpen}
          onClose={() => {
            setIsBookingModalOpen(false);
            setSelectedTour(null);
          }}
          tourTitle={selectedTour.title}
          tourPrice={selectedTour.price}
          tourDuration={selectedTour.duration}
        />
      )}

      <Footer />
    </>
  );
};

export default UgandaCityTours;